import { useQuery } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import { Card, CardHeader } from '@truecare/ui';
import { api } from '../api';

/**
 * Bug #7 (Phase 17) — the caregiver's own copy of their signed independent-
 * contractor agreement. The backend already returns `signedPdfUrl` on
 * GET /me/caregiver/contract (Phase 23-4), but no portal surface exposed it: the
 * accept card only renders BEFORE acceptance, so once signed the agreement
 * vanished and the signatory had no way to retrieve it. Shown on the /me Docs
 * status tab (reuses the ['caregiver','contract'] query the accept card uses).
 */
export function CaregiverSignedAgreementCard() {
  const q = useQuery({
    queryKey: ['caregiver', 'contract'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/contract')),
  });
  if (q.isPending || q.isError || !q.data) return null;
  const { accepted, acceptedAt, acceptedVersion, signedPdfUrl, title } = q.data;
  // Nothing signed yet → the accept card (onboarding) handles that state.
  if (!accepted || !acceptedAt) return null;

  const signedDate = new Date(acceptedAt).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Card padding="lg" accent="left" tone="forest" data-testid="caregiver-signed-agreement">
      <CardHeader
        title="Signed agreement"
        description="Your accepted independent-contractor agreement — keep a copy for your records."
      />
      <div className="mt-3 font-body text-sm text-ink">
        <p>
          <strong>{title || 'Independent-contractor agreement'}</strong>
          {acceptedVersion ? ` (${acceptedVersion})` : ''}
        </p>
        <p className="text-ink-muted">Signed {signedDate}</p>
        <div className="mt-3">
          {signedPdfUrl ? (
            <a
              href={signedPdfUrl}
              target="_blank"
              rel="noopener"
              className="font-semibold text-forest underline underline-offset-2"
              data-testid="caregiver-signed-agreement-download"
            >
              Download signed copy (PDF) →
            </a>
          ) : (
            <p className="text-ink-muted">
              Your signed copy is being prepared — check back shortly.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
